import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { analiseApi, votosOrfaosApi } from '@/lib/api/analise';
import { alertasApi } from '@/lib/api/core';
import { liderancasApi } from '@/lib/api/liderancas';
import { agentesApi, fontesApi } from '@/lib/api/oto';
import { personaApi } from '@/lib/api/persona';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros, type BairroView } from '@/lib/hooks/useBairros';
import { useMockStore } from '@/lib/stores/mockStore';
import { Info } from '@/design-system/Info';

interface Resposta {
  fato: string;
  calculo: string;
  interpretacao: string;
  fontes: string;
}

const brl = (v: number) =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/**
 * Atalho para uma pergunta que a tela sabe responder, com a contagem do que ele
 * traz. O rótulo é montado; a pergunta enviada é só `pergunta` — o número não
 * entra no texto do diálogo.
 */
interface Sugestao {
  pergunta: string;
  detalhe: string;
}

/** Liderança que pede atenção: inativa, ou saúde declarada abaixo de 60. */
const PISO_SAUDE = 60;

export function AgentePage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const bairroAtivo = useMockStore((s) => s.bairroActiveId);
  const { bairros } = useBairros();

  const [pergunta, setPergunta] = useState('');
  const [historico, setHistorico] = useState<Array<{ q: string; agente: string; r: Resposta }>>([]);
  const [agenteAtivo, setAgenteAtivo] = useState('');

  const { data: dashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analiseApi.dashboard(token),
    enabled: !!token,
  });

  const { data: matriz = [] } = useQuery({
    queryKey: ['matriz'],
    queryFn: () => analiseApi.matriz(token),
    enabled: !!token,
  });

  const { data: alertas = [] } = useQuery({
    queryKey: ['alertas'],
    queryFn: () => alertasApi.list(token),
    enabled: !!token,
  });

  const { data: agentes = [] } = useQuery({
    queryKey: ['agentes'],
    queryFn: () => agentesApi.list(token),
    enabled: !!token,
  });

  const { data: fontes = [] } = useQuery({
    queryKey: ['fontes', {}],
    queryFn: () => fontesApi.list(token),
    enabled: !!token,
  });

  const { data: persona } = useQuery({
    queryKey: ['persona'],
    queryFn: () => personaApi.get(token),
    enabled: !!token,
  });

  const { data: liderancas = [] } = useQuery({
    queryKey: ['liderancas', {}],
    queryFn: () => liderancasApi.list({}, token),
    enabled: !!token,
  });

  const { data: orfaos = [] } = useQuery({
    queryKey: ['votos-orfaos', 'resumo'],
    queryFn: () => votosOrfaosApi.resumo(token),
    enabled: !!token,
  });

  const ativos = agentes.filter((a) => a.ativo);
  const territorioAtual: BairroView | undefined =
    bairros.find((b) => b.id === bairroAtivo) || bairros[0];

  const criticos = alertas.filter((a) => a.severidade === 'critica');
  // Célula suprimida por k-anonimato não conta como lacuna: o número está
  // escondido justamente porque identificaria gente.
  const lacunas = matriz.filter((c) => !c.suprimido && (c.lacuna ?? 0) > 0);
  const prioridades = dashboard?.prioridades ?? [];
  const liderancasEmAtencao = liderancas.filter(
    (l) => l.status === 'inativa' || (l.saude ?? 100) < PISO_SAUDE,
  );
  const totalOrfaos = orfaos.reduce((s, o) => s + o.total, 0);

  /**
   * Monta a resposta a partir dos dados reais do mandato. Não há modelo de
   * linguagem por trás: cada resposta declara o fato consultado, o cálculo e a
   * origem, para que a leitura seja verificável.
   */
  const responder = (q: string): Resposta => {
    const texto = q.toLowerCase();

    if (texto.includes('lideranç') || texto.includes('lideranc')) {
      const amostra = liderancasEmAtencao
        .slice(0, 3)
        .map((l) => `${l.nome} (${l.status === 'inativa' ? 'inativa' : `saúde ${l.saude}`})`);
      return {
        fato: liderancasEmAtencao.length
          ? `${liderancasEmAtencao.length} de ${liderancas.length} liderança(s) pedindo atenção: ${amostra.join(' | ')}`
          : `${liderancas.length} liderança(s) cadastrada(s), todas ativas e com saúde ${PISO_SAUDE} ou mais.`,
        calculo:
          `Atenção = status "inativa" ou saúde abaixo de ${PISO_SAUDE}. A saúde é o número que ` +
          'o gabinete digita na tela de Lideranças; não é score calculado por visita ou contato.',
        interpretacao: liderancasEmAtencao.length
          ? `Comece por ${liderancasEmAtencao[0]!.nome}: liderança sem vínculo ativo deixa o território sem quem convoque.`
          : 'A rede de lideranças está com a situação em dia segundo o que o gabinete registrou.',
        fontes: 'gab.lideranca',
      };
    }

    if (
      texto.includes('branco') ||
      texto.includes('nulo') ||
      texto.includes('abstenç') ||
      texto.includes('órfão') ||
      texto.includes('orfao')
    ) {
      const ordenados = [...orfaos].sort(
        (a, b) => b.percentual_eleitorado - a.percentual_eleitorado,
      );
      const topo = ordenados.slice(0, 3);
      return {
        fato: totalOrfaos
          ? `${totalOrfaos} voto(s) sem destino em ${orfaos.length} território(s). Maior incidência: ` +
            topo.map((o) => `${o.bairro} (${o.total}, ${o.percentual_eleitorado}% do eleitorado)`).join(' | ')
          : 'Nenhum branco, nulo ou abstenção registrado para o ciclo.',
        calculo:
          'Soma de brancos, nulos, abstenções e desistentes lançados por território, sobre o ' +
          'eleitorado do território. São os números que o gabinete cadastrou em Votos órfãos, sem importação automática do TSE.',
        interpretacao: topo.length
          ? `${topo[0]!.bairro} concentra a maior fatia de eleitorado que não escolheu ninguém: é o voto disputável sem tirar de adversário.`
          : 'Sem lançamento não há leitura: cadastre o ciclo em Votos órfãos.',
        fontes: 'eleg.voto_orfao · terr.bairro',
      };
    }

    if (texto.includes('alerta') || texto.includes('risco') || texto.includes('crítico')) {
      return {
        fato: criticos.length
          ? `${criticos.length} condição(ões) crítica(s) ativa(s): ${criticos.map((a) => a.titulo).join(' | ')}`
          : 'Nenhuma condição crítica ativa no momento.',
        calculo: `${alertas.length} regra(s) disparada(s) no total, avaliadas sobre território, lideranças, ações, solicitações e contatos.`,
        interpretacao: criticos.length
          ? 'Priorize a resolução das críticas: são territórios com alta necessidade sem entrega registrada ou SLA estourado.'
          : 'Sem urgências. Vale olhar as oportunidades listadas na tela de alertas.',
        fontes: 'eleg.alertas · terr.bairro · gab.acao · gab.solicitacao',
      };
    }

    if (texto.includes('demanda') || texto.includes('resposta') || texto.includes('lacuna')) {
      // Celula suprimida nao vai para o agente: o texto gerado sairia com o
      // numero que a tela esconde, e o agente e lido por quem nao tem acesso
      // aos registros.
      const maiores = lacunas.slice(0, 3);
      return {
        fato: maiores.length
          ? maiores.map((c) => `${c.bairro}/${c.tema}: ${c.demanda} pedido(s), ${c.cobertura} ação(ões)`).join(' | ')
          : 'Nenhum cruzamento com demanda acima da cobertura.',
        calculo: `Matriz com ${matriz.length} cruzamento(s) território × tema. Lacuna = solicitações menos ações cadastradas.`,
        interpretacao: maiores.length
          ? `A maior lacuna está em ${maiores[0]!.bairro}, no tema ${maiores[0]!.tema}. É onde uma ação nova tem mais retorno por esforço.`
          : 'Toda demanda registrada tem ação correspondente cadastrada.',
        fontes: 'terr.analise/matriz · gab.solicitacao · gab.acao',
      };
    }

    if (texto.includes('contato') || texto.includes('base') || texto.includes('opt')) {
      const c = dashboard?.contatos;
      return {
        fato: `${c?.total ?? 0} contato(s) na base, ${c?.com_optin ?? 0} com opt-in e ${c?.sem_base ?? 0} sem base legal.`,
        calculo: `Só os ${c?.com_optin ?? 0} com opt-in podem receber campanha ou régua. Os demais só por ligação ou contato presencial.`,
        interpretacao:
          (c?.sem_base ?? 0) > (c?.com_optin ?? 0) / 2
            ? 'Parcela relevante da base está fora do alcance de comunicação. Vale um esforço de coleta de consentimento.'
            : 'A base tem cobertura de consentimento saudável para comunicação.',
        fontes: 'gab.contato · terr.analise/dashboard',
      };
    }

    if (texto.includes('presença') || texto.includes('investir') || texto.includes('prioridade')) {
      const p = prioridades;
      const semEntrega = p.filter((x) => x.acoes_concluidas === 0);
      return {
        fato: p.length
          ? p.slice(0, 3).map((x) => `${x.bairro}: necessidade ${x.necessidade}/100, ${x.acoes_concluidas} ação(ões) concluída(s)`).join(' | ')
          : 'Nenhum território cadastrado.',
        calculo: `${semEntrega.length} dos ${p.length} territórios prioritários não têm nenhuma entrega registrada.`,
        interpretacao: semEntrega.length
          ? `${semEntrega[0]!.bairro} combina a maior necessidade com ausência de entrega: é a maior oportunidade de presença.`
          : 'Todos os territórios prioritários já têm ao menos uma entrega registrada.',
        fontes: 'terr.bairro · gab.acao · terr.analise/dashboard',
      };
    }

    if (texto.includes('território') || texto.includes('territorio') || texto.includes('bairro')) {
      const b = territorioAtual;
      if (!b) {
        return {
          fato: 'Nenhum território cadastrado para este mandato.',
          calculo: '—',
          interpretacao: 'Cadastre os territórios de atuação para que eu possa analisá-los.',
          fontes: 'terr.bairro',
        };
      }
      return {
        fato: `${b.n}: ${b.pop.toLocaleString('pt-BR')} moradores, ${b.eleit.toLocaleString('pt-BR')} eleitores, ${b.votos.toLocaleString('pt-BR')} votos (${b.pct}%), ${b.contatosCadastrados} contato(s) e ${b.liderancasAtivas} liderança(s) ativa(s).`,
        calculo: `Índice de necessidade ${b.nec}/100. Fila de consulta em ${b.fila} dias, ${b.escuros} ponto(s) escuro(s), ${b.creche} vaga(s) de creche em falta. ${b.solicitacoesAbertas} solicitação(ões) aberta(s).`,
        interpretacao:
          b.nec > 70
            ? 'Território de alta carência: entrega visível aqui tem alto retorno reputacional.'
            : 'Carência moderada: o foco pode ser consolidação de base em vez de obra estruturante.',
        fontes: 'terr.bairro · gab.contato · gab.lideranca · gab.solicitacao',
      };
    }

    // Resposta padrão: panorama do mandato
    return {
      fato: `${dashboard?.contatos.total ?? 0} contatos, ${dashboard?.solicitacoes.abertas ?? 0} solicitação(ões) aberta(s), ${dashboard?.acoes.total ?? 0} ação(ões) e ${brl(dashboard?.emendas.indicado ?? 0)} em emendas indicadas.`,
      calculo: `${dashboard?.solicitacoes.sla_vencido ?? 0} solicitação(ões) com SLA vencido. ${dashboard?.territorios.sem_acao_concluida ?? 0} território(s) prioritário(s) sem entrega.`,
      interpretacao:
        'Pergunte sobre território, presença, demandas sem resposta, alertas ou base de contatos para uma leitura específica.',
      fontes: 'terr.analise/dashboard',
    };
  };

  /**
   * As sete sugestões do protótipo (proto:1024), refeitas sobre dado real.
   *
   * Três das perguntas do protótipo não sobreviveram, e a razão é a mesma nas
   * três — não existe número no banco que as responda:
   *
   * - "Como evoluiu a votação da Ana Silva?": nomeia a candidata da
   *   demonstração e pede série histórica por eleição; `terr.bairro` guarda
   *   `votos`/`percentual` de um ciclo só, sem o anterior para comparar.
   * - "O que dizem as pesquisas sobre o momento?": a tela de Pesquisas tem o
   *   dado e a leitura com metodologia e margem; repetir aqui seria resumo de
   *   segunda mão, sem os recortes que tornam a comparação válida.
   * - "Quantos votos terei em 2028?": projeção. Responder exigiria inventar um
   *   número com aparência de análise, que é o oposto do que esta tela faz.
   *
   * As outras duas ("lideranças" e "brancos e nulos") têm lastro e entraram,
   * com ramo próprio em `responder`. Cada chip só aparece quando os registros
   * que ele consulta existem, e diz quantos são — um atalho que volta vazio só
   * gasta o clique de quem confiou nele.
   */
  const sugestoes: Sugestao[] = [
    prioridades.length > 0 && {
      pergunta: 'Onde devo investir presença?',
      detalhe: `${prioridades.length} território(s) prioritário(s)`,
    },
    territorioAtual && {
      pergunta: `Como está ${territorioAtual.n}?`,
      detalhe: `necessidade ${territorioAtual.nec}/100`,
    },
    alertas.length > 0 && {
      pergunta: criticos.length ? 'Quais são os alertas críticos?' : 'Quais alertas estão ativos?',
      detalhe: criticos.length
        ? `${criticos.length} crítico(s) de ${alertas.length}`
        : `${alertas.length} ativo(s), nenhum crítico`,
    },
    lacunas.length > 0 && {
      pergunta: 'Onde a demanda não tem resposta?',
      detalhe: `${lacunas.length} cruzamento(s) com lacuna`,
    },
    (dashboard?.contatos.total ?? 0) > 0 && {
      pergunta: 'Como está a base de contatos?',
      detalhe: `${dashboard!.contatos.total} contato(s)`,
    },
    liderancas.length > 0 && {
      pergunta: 'Quais lideranças precisam de atenção?',
      detalhe: liderancasEmAtencao.length
        ? `${liderancasEmAtencao.length} em atenção`
        : `${liderancas.length} cadastrada(s), nenhuma em atenção`,
    },
    totalOrfaos > 0 && {
      pergunta: 'Onde estão os brancos e nulos?',
      detalhe: `${totalOrfaos} voto(s) em ${orfaos.length} território(s)`,
    },
  ].filter((s): s is Sugestao => typeof s === 'object' && s !== null);

  const perguntar = (texto?: string) => {
    const q = (texto ?? pergunta).trim();
    if (!q) return;
    setHistorico((prev) => [
      ...prev,
      { q, agente: agenteAtivo || ativos[0]?.nome || 'OTO', r: responder(q) },
    ]);
    setPergunta('');
  };

  return (
    <div className="page" id="p-agente">
      <div className="head">
        <div>
          <h1 style={{ color: '#B26A00' }}>Agente analista</h1>
          <p>
            {/*
              proto:1022 acrescenta "cita fonte e período". Fonte sim — a
              resposta carrega as entidades de origem; período não: `Resposta`
              não tem esse campo, e inventar a promessa aqui seria repor
              exatamente o que este plano passou a sessão removendo.
            */}
            Perguntas em linguagem natural sobre os dados do mandato. Toda resposta separa fato,
            cálculo e interpretação, cita as entidades de origem e declara quando os dados não
            bastam. Nunca altera dados.
          </p>
        </div>
      </div>

      <div className="note" style={{ marginBottom: 12, fontSize: 12 }}>
        <b>Não há modelo de linguagem aqui.</b> As respostas saem de consultas ao banco do
        mandato, com regras determinísticas.
        <Info titulo="O que a ausência de modelo garante">
          A mesma pergunta devolve a mesma resposta sempre, e cada número traz a entidade de onde
          saiu. Nada é gerado, redigido nem estimado por um modelo.
        </Info>
      </div>

      <div className="grid g21">
        {/* Conversa */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', minHeight: 460 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>Diálogo</h2>
            <select
              className="sel sm"
              value={agenteAtivo}
              onChange={(e) => setAgenteAtivo(e.target.value)}
            >
              <option value="">Agente automático</option>
              {ativos.map((a) => (
                <option key={a.id} value={a.nome}>
                  {a.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="msgbox" style={{ flex: 1, maxHeight: 380, overflowY: 'auto', margin: '12px 0' }}>
            {historico.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--ink3)' }}>
                Faça uma pergunta ou use uma das sugestões abaixo.
              </div>
            )}

            {historico.map((h, i) => (
              <div key={i} style={{ marginBottom: 14 }}>
                <div className="msg me" style={{ marginBottom: 6 }}>
                  {h.q}
                </div>
                <div className="msg" style={{ background: 'var(--warn-soft)', maxWidth: '95%' }}>
                  <b style={{ color: '#B26A00' }}>{h.agente}</b>
                  <dl className="dl" style={{ marginTop: 6, fontSize: 12 }}>
                    <dt>Fato</dt>
                    <dd>{h.r.fato}</dd>
                    <dt>Cálculo</dt>
                    <dd>{h.r.calculo}</dd>
                    <dt>Leitura</dt>
                    <dd>{h.r.interpretacao}</dd>
                    <dt>Origem</dt>
                    <dd className="mono" style={{ fontSize: 11 }}>
                      {h.r.fontes}
                    </dd>
                  </dl>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="in"
              placeholder="Pergunte sobre território, presença, demandas, alertas ou base..."
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && perguntar()}
            />
            <button className="btn pri" onClick={() => perguntar()}>
              Perguntar
            </button>
          </div>

          <div className="filters" style={{ marginTop: 10, flexWrap: 'wrap' }}>
            {sugestoes.map((s) => (
              <span
                key={s.pergunta}
                className="chip neutral chipbtn"
                onClick={() => perguntar(s.pergunta)}
              >
                {s.pergunta} · {s.detalhe}
              </span>
            ))}
          </div>

          {sugestoes.length < 7 && (
            <div className="note warn" style={{ fontSize: 11, marginTop: 8 }}>
              {sugestoes.length} das 7 sugestões: as demais dependem de territórios, alertas,
              solicitações, contatos, lideranças e votos brancos/nulos cadastrados neste mandato.
              Nenhuma pergunta de exemplo entra no lugar.
            </div>
          )}
        </div>

        {/* Contexto */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card">
            <h2 style={{ fontSize: 14, margin: 0 }}>Contexto em uso</h2>
            <dl className="dl" style={{ marginTop: 8, fontSize: 12 }}>
              <dt>Território ativo</dt>
              <dd>{territorioAtual?.n || '—'}</dd>
              <dt>Persona</dt>
              <dd>
                tom {persona?.tom || '—'} · {persona?.completude ?? 0}% preenchida
              </dd>
              <dt>Agentes ativos</dt>
              <dd>{ativos.length}</dd>
              <dt>Fontes carregadas</dt>
              <dd>{fontes.length}</dd>
            </dl>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button className="btn sm" onClick={() => navigate('/perfil')}>
                Calibrar persona
              </button>
              <button className="btn sm" onClick={() => navigate('/fontes')}>
                Fontes
              </button>
            </div>
          </div>

          {persona?.limites && persona.limites.length > 0 && (
            <div className="card">
              <h2 style={{ fontSize: 14, margin: 0 }}>Limites de IA Segura</h2>
              <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 8px' }}>
                Temas que o mandato evita, respeitados por todos os agentes.
              </p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {persona.limites.map((l) => (
                  <span key={l} className="chip crit" style={{ fontSize: 11 }}>
                    {l}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="card kpi">
            <div className="l">Alertas ativos</div>
            <div className="v" style={{ color: alertas.length ? 'var(--warn)' : 'var(--good)' }}>
              {alertas.length}
            </div>
            <div className="d">condições vigentes no mandato</div>
            <button className="btn sm" style={{ marginTop: 10 }} onClick={() => navigate('/alertas')}>
              Ver alertas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
